import type { Message } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { Play, Send } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/client';
import { usePostMessage, useRunSession, useSession } from '@/lib/api/sessions';

import { OriginBadge } from './origin-badge';
import { StatusBadge } from './status-badge';
import { Transcript } from './transcript';

// A run has something to do when there's a user prompt after the last response —
// mirrors the server's pending-queue check so the button reflects the 409 rule.
function hasPendingUserPrompt(messages: Message[]): boolean {
  let lastResponseSeq = -1;
  for (const message of messages) {
    if (message.kind === 'response') {
      lastResponseSeq = Math.max(lastResponseSeq, message.seq);
    }
  }
  return messages.some(
    (message) =>
      message.kind === 'request' &&
      message.seq > lastResponseSeq &&
      message.parts.some((part) => part.partKind === 'user-prompt'),
  );
}

export function SessionDetailPage({ sessionId }: { sessionId: string }) {
  const sessionQuery = useSession(sessionId);
  const postMessage = usePostMessage(sessionId);
  const runSession = useRunSession(sessionId);

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (sessionQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (sessionQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load session: {sessionQuery.error.message}
      </p>
    );
  }

  const session = sessionQuery.data;
  // An interrupted session blocks the same controls a running one does: its transcript can end
  // on a tool call with no return, so the server rejects an append until the run is resumed.
  const running = session.status === 'running' || session.status === 'interrupted';
  const pending = hasPendingUserPrompt(session.messages);

  const reportError = (caught: unknown, fallback: string) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const queue = () => {
    const content = draft.trim();
    if (!content) {
      return;
    }
    setError(null);
    postMessage.mutate(content, {
      onSuccess: () => setDraft(''),
      onError: (caught) => reportError(caught, 'Could not queue the message.'),
    });
  };

  const run = () => {
    setError(null);
    runSession.mutate(undefined, {
      onError: (caught) => reportError(caught, 'Could not run the session.'),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {session.name ?? 'Untitled session'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <StatusBadge status={session.status} />
            <Badge variant="secondary">{session.agentVersion.slug}</Badge>
            <span>v{session.agentVersion.versionNumber}</span>
            <span>·</span>
            <span>{session.agentVersion.modelTier}</span>
            <span>·</span>
            <span className="tabular-nums">
              {session.tokensIn} in ({session.cacheReadTokens} cached,{' '}
              {session.tokensIn - session.cacheReadTokens} uncached) / {session.tokensOut} out
            </span>
          </div>
          {session.createdByEmail && (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Created by {session.createdByEmail}
            </p>
          )}
        </div>
        <Button variant="outline" asChild>
          <Link to="/sessions">Back</Link>
        </Button>
      </div>

      {session.error && (
        <p className="text-sm text-[var(--color-destructive)]">Last run failed: {session.error}</p>
      )}

      {session.status === 'interrupted' && (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          This run stopped when its server shut down. The transcript is intact — the gateway sweep
          continues it from where it left off.
        </p>
      )}

      {(session.origin || session.children.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Lineage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {session.origin && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[var(--color-muted-foreground)]">Origin</span>
                {session.originKind && <OriginBadge kind={session.originKind} />}
                <Link
                  to="/sessions/$sessionId"
                  params={{ sessionId: session.origin.id }}
                  className="font-medium hover:underline"
                >
                  {session.origin.name ?? 'Untitled session'}
                </Link>
              </div>
            )}
            <div className="space-y-1">
              <span className="text-[var(--color-muted-foreground)]">Children</span>
              {session.children.length ? (
                <ul className="space-y-1">
                  {session.children.map((child) => (
                    <li key={child.id} className="flex flex-wrap items-center gap-2">
                      <OriginBadge kind={child.originKind} />
                      <Link
                        to="/sessions/$sessionId"
                        params={{ sessionId: child.id }}
                        className="font-medium hover:underline"
                      >
                        {child.name ?? 'Untitled session'}
                      </Link>
                      <StatusBadge status={child.status} />
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-[var(--color-muted-foreground)]">None</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Transcript</CardTitle>
        </CardHeader>
        <CardContent>
          <Transcript messages={session.messages} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Compose</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="Queue a user message, then run the agent…"
            className="min-h-24"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={running}
          />
          {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={queue}
              disabled={running || postMessage.isPending || !draft.trim()}
            >
              <Send className="size-4" />
              {postMessage.isPending ? 'Queuing…' : 'Queue message'}
            </Button>
            <Button onClick={run} disabled={running || runSession.isPending || !pending}>
              <Play className="size-4" />
              {runSession.isPending ? 'Running…' : 'Run'}
            </Button>
            {!pending && !running && (
              <span className="text-xs text-[var(--color-muted-foreground)]">
                Queue a message to enable run.
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
